/**
 * Resolves a theme role such as `--foreground` to a concrete `rgb()` color for code that cannot
 * read CSS variables: canvas, iframes, or a choice between two colors.
 */
export const resolveTokenColor = (token: string): string => {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.style.display = 'none';
  document.body.append(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  return color;
};

const luminance = (color: string): number | null => {
  const channels = color
    .match(/\d+(\.\d+)?/g)
    ?.slice(0, 3)
    .map(Number);
  if (channels?.length !== 3) {
    return null;
  }
  const [r, g, b] = channels.map((value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG 2.2 contrast ratio of two `rgb()` colors, or 0 when a color cannot be read. */
export const contrastRatio = (a: string, b: string): number => {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) {
    return 0;
  }
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const WHITE = 'rgb(255, 255, 255)';
const BLACK = 'rgb(0, 0, 0)';

/** White or black, whichever has more contrast with `fill`. One of the two always reaches 4.5:1. */
export const readableOn = (fill: string): 'white' | 'black' =>
  contrastRatio(BLACK, fill) > contrastRatio(WHITE, fill) ? 'black' : 'white';
